/**
 * Журнал оповещений: что будило человека.
 *
 * Раньше здесь была лента всех сигналов со своими фильтрами LONG/SHORT
 * и своими стрелками на графике. Она дублировала индикаторы: те уже
 * рисуют свои отметки сами, и одно событие оказывалось на свече дважды,
 * в двух разных видах. Теперь сигнал — язык индикатора и бота, а журнал
 * — история сработавших оповещений, и только их.
 *
 * Оповещение — это сработка, о которой человек просил сказать: цена
 * дошла до уровня, подписанный индикатор дал событие. Признак один и
 * общий с сервером — `meta.rule.notify`: ровно по нему сервер решает,
 * слать ли в Telegram.
 */

import { PRICE_ALERT_NAME, signalSourceName, type Signal } from '@specter/types';
import { priceWords } from '../indicators/explain.js';

/** Сколько записей держать: журнал отвечает «что было недавно», а не хранит всё. */
const LOG_LIMIT = 300;

export type JournalKind = 'price' | 'indicator' | 'radar' | 'bot';

export interface JournalEntry {
  readonly id: string;
  readonly kind: JournalKind;
  /** Что случилось — словами, как в уведомлении. */
  readonly text: string;
  /** Откуда: «Оповещение по цене», имя индикатора. */
  readonly source: string;
  /**
   * Что сказал индикатор, если у сигнала есть сторона: лонг или шорт.
   * `null` — событие без стороны: цена дошла до уровня, линии пересеклись.
   */
  readonly side: 'long' | 'short' | null;
  readonly venue: string;
  readonly symbol: string;
  readonly timeframe: string;
  readonly at: number;
  readonly barTime: number;
  readonly signal: Signal;
}

/**
 * Сработка, о которой человек просил сказать, — или сделка его бота.
 *
 * Бот с владельцем — личный: его события (сделка, «уведомить») уходят
 * хозяину в Telegram, и журнал показывает ровно их же. Общие боты
 * платформы владельца не имеют и сюда не попадают — это лента, а не
 * оповещения.
 */
export function isAlertEvent(signal: Signal): boolean {
  if (signal.status === 'INVALIDATED' || signal.origin === 'history') return false;
  if (signal.sourceKind === 'bot') return signal.owner !== undefined;
  return signal.sourceKind === 'rule' && signal.meta?.rule?.notify === true;
}

/** Откуда событие: цена, индикатор, радар или бот. */
export function journalKind(signal: Signal): JournalKind {
  if (isPriceAlertEvent(signal)) return 'price';
  if (signal.sourceKind === 'bot' || signal.botId === 'world') return 'bot';
  if (signal.botId.startsWith('radar:')) return 'radar';
  return 'indicator';
}

/** Сработка оповещения по цене — у неё на графике свой колокольчик. */
export function isPriceAlertEvent(signal: Signal): boolean {
  return signal.id.startsWith('price-') && signal.meta?.rule?.ruleName === PRICE_ALERT_NAME;
}

/** Слить пришедшее с известным: без повторов, новые сверху, с пределом. */
export function mergeAlertLog(current: readonly Signal[], incoming: readonly Signal[]): Signal[] {
  const byId = new Map(current.map((signal) => [signal.id, signal]));
  for (const signal of incoming) {
    if (isAlertEvent(signal)) byId.set(signal.id, signal);
  }
  return [...byId.values()].sort((a, b) => b.createdAt - a.createdAt).slice(0, LOG_LIMIT);
}

/** Записи журнала в том виде, в каком их читает человек. */
export function journalEntries(log: readonly Signal[], clearedAt: number): JournalEntry[] {
  return log
    .filter((signal) => signal.createdAt > clearedAt)
    .map((signal) => {
      const rule = signal.meta?.rule;
      const kind = journalKind(signal);
      const source = signalSourceName(signal);
      const side = signal.side === 'LONG' ? 'long' : signal.side === 'SHORT' ? 'short' : null;
      /*
       * Текст — словами, а не «Выгодные зоны PRO · цена 2632.48».
       *
       * Если источник оставил объяснение — оно. Если нет, но есть
       * сторона, — сторону покажет пилюля рядом, а текст скажет, по какой
       * цене: «по 2 632,5». Имя источника и так стоит строкой ниже.
       */
      const text = rule?.plain ?? signal.meta?.comment ?? (side ? `по ${priceWords(signal.price)}` : `цена ${priceWords(signal.price)}`);
      return {
        id: signal.id,
        kind,
        text,
        source,
        side,
        venue: signal.venue,
        symbol: signal.symbol,
        timeframe: signal.timeframe,
        at: signal.createdAt,
        barTime: signal.barTime,
        signal,
      } satisfies JournalEntry;
    });
}
